import API from "@/api/wrapper";
import {
  APIClusterRequirement,
  APIClusterNodeTypologie,
  APIUrlRedirection,
  APIClusterLocation,
  APILoadBalancerTypologie,
  APICluster,
} from "@/typesAPI";
import enums from "@/enums";
import store from "@/store";
import worldCountries, { Country } from "world-countries";
import { diff, applyChangeset } from "json-diff-ts";

function findCountryByDigram(digram: string) {
  return worldCountries.find((v) => v.cca2 === digram);
}
function orderJSON(json: { [key: string]: any }): { [key: string]: any } {
  const keys = Object.keys(json);
  const orderedKeys = keys.sort((a, b) => a.localeCompare(b));
  const orderedJSON: { [key: string]: any } = {};
  orderedKeys.forEach((key: string) => {
    orderedJSON[key] = json[key];
  });
  return orderedJSON;
}

function reconstructJSONFromDiff(changeset: any) {
  const newJSON = {};
  changeset.forEach((change: any) => {
    addJSONDiffChange(newJSON, change);
  });
  return newJSON;
}

function addJSONDiffChange(json: any, change: any) {
  if (change.value !== undefined) {
    if (change.type === "REMOVE") {
      json[change.key] = undefined;
    } else {
      json[change.key] = change.value;
    }
  } else if (change.changes !== undefined) {
    json[change.key] = change.embeddedKey === "$index" ? [] : {};
    change.changes.forEach((subChange: any) => {
      addJSONDiffChange(json[change.key], subChange);
    });
  }
}

function removeDeleteKeyFromDiffDescriptor(diffDescriptor: any[]) {
  diffDescriptor.forEach((change: any, index: number) => {
    if (change.type === "REMOVE") {
      delete diffDescriptor[index];
    } else if (change.changes !== undefined) {
      removeDeleteKeyFromDiffDescriptor(change.changes);
    }
  });
  return diffDescriptor;
}

// Console
interface Console {
  log: any;
  logLevel: number;
}

// Ressources
interface metaGenerated {
  id: string;
  meta: {
    generated_at: string;
    generated_by: string;
  };
}

const helpers = {
  static: {
    stripeAppearance: {
      theme: "stripe",
      variables: {
        fontFamily: "Inter, sans-serif",
        fontWeightNormal: 400,
        fontSizeBase: "14px",
        gridRowSpacing: "0",
      },
      rules: {
        ".Tab": {
          marginBottom: "16px",
          boxShadow:
            "0px 1px 4px rgba(0, 0, 58, 0.08), 0px 1px 1px -1px rgba(0, 0, 58, 0.08), inset 0px -1px 0px rgba(0, 0, 0, 0.08);",
          border: "1px solid rgba(0, 0, 58, 0.12)",
        },
        ".Tab--selected": {
          border: "2px solid rgba(50, 25, 178, 1)",
          boxShadow: "0px 0px 0px 2px rgba(50, 25, 178, 0.24)",
        },
        ".Tab--selected:focus": {
          border: "2px solid rgba(50, 25, 178, 1)",
          boxShadow: "0px 0px 0px 2px rgba(50, 25, 178, 0.24)",
        },
        ".TabLabel--selected": {
          color: "rgba(50, 25, 178, 1)",
        },
        ".Label": {
          marginBottom: "0px",
          fontFamily: "Inter, sans-serif",
          lineHeight: "24px",
          fontSize: "14px",
        },
        ".Input": {
          marginTop: "8px",
          marginBottom: "16px",
          boxShadow:
            "0px 1px 4px rgba(0, 0, 58, 0.08), 0px 1px 1px -1px rgba(0, 0, 58, 0.08), inset 0px -1px 0px rgba(0, 0, 0, 0.08);",
          borderRadius: "8px",
          color: "#00003A",
          outline: "none",
          padding: "12px",
          border: "1px solid rgba(0, 0, 58, 0.12)",
          transition:
            "box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out",
        },
        ".Input:hover": {
          // border: "1px solid rgba(0, 0, 58, 0.30)"
        },
        ".Input:focus": {
          paddingLeft: "11px",
          marginTop: "7px",
          marginBottom: "15px",
          border: "2px solid rgba(50, 25, 178, 1)",
          boxShadow: "0px 0px 0px 2px rgba(50, 25, 178, 0.24)",
        },
      },
    },
  },
  text: {
    capitalize(txt: string) {
      return txt ? txt?.charAt(0).toUpperCase() + txt?.slice(1) : "";
    },
  },
  units: {
    bytesToHumanReadable(bytes: number): string {
      const sizes = ["B", "KB", "MB", "GB", "TB"];
      if (bytes === 0) return "0 B";
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return (
        Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + " " + sizes[i]
      );
    },
  },
  clusters: {
    getNbNodes(cluster?: APICluster): { min: number; max: number } {
      if (!cluster) {
        return { min: 0, max: 0 };
      }
      const nodePoolList = cluster?.attributes?.requested_config?.node_pools;
      const isAutoPilot =
        cluster?.attributes?.requested_config?.auto_pilot?.enabled;
      if (!nodePoolList?.length) {
        return { min: 0, max: 0 };
      }
      if (!isAutoPilot) {
        const n = nodePoolList.reduce(
          (accumulator, val) => accumulator + (val.target_size ?? 0),
          0,
        );
        return { min: n, max: n };
      }
      return {
        min: nodePoolList.reduce(
          (accumulator, val) => accumulator + (val.min_size ?? 0),
          0,
        ),
        max: nodePoolList.reduce(
          (accumulator, val) => accumulator + (val.max_size ?? 0),
          0,
        ),
      };
    },
    getTypologiesFromRequirements(
      requirements: APIClusterRequirement[],
      typologies: (APIClusterNodeTypologie | APILoadBalancerTypologie)[],
      nbNodes: number,
      type: string,
      criticality?: string,
    ): (APIClusterNodeTypologie | APILoadBalancerTypologie)[] {
      const ret: typeof typologies = [];
      this.getMatchingRequirements(
        requirements,
        nbNodes,
        type,
        criticality,
      ).forEach((requirement) => {
        const typology = typologies.find(
          (typo: (typeof typologies)[number]) =>
            typo.id === requirement.attributes.details.typology,
        );
        if (typology) {
          for (let i = 0; i < requirement.attributes.details.amount; i++) {
            ret.push(typology);
          }
        }
      });
      return ret;
    },
    getMatchingRequirements(
      requirements: APIClusterRequirement[],
      nbNodes: number,
      type: string,
      criticality?: string,
    ): APIClusterRequirement[] {
      if (nbNodes === undefined) {
        return [];
      }

      const matches = requirements.filter((requirement) => {
        const conditions = requirement.attributes.conditions;
        const requirementCriticality = conditions.criticality;
        const isGenericCriticality = !requirementCriticality;
        const isMatchingCriticality = requirementCriticality === criticality;
        const isSupMin =
          conditions.min_nodes == null || conditions.min_nodes <= nbNodes;
        const isInfMax =
          conditions.max_nodes == null || conditions.max_nodes >= nbNodes;

        return (
          requirement.attributes.type === type &&
          isSupMin &&
          isInfMax &&
          (isGenericCriticality || isMatchingCriticality)
        );
      });

      const criticalityMatches = matches.filter(
        (requirement) => requirement.attributes.conditions.criticality,
      );
      return criticalityMatches.length ? criticalityMatches : matches;
    },
    getMinAmountFromRequirements(
      requirements: APIClusterRequirement[],
      nbNodes: number,
      type: string,
      criticality?: string,
      defaultValue = 1,
    ): number {
      const matchingRequirements = this.getMatchingRequirements(
        requirements,
        nbNodes,
        type,
        criticality,
      );
      if (!matchingRequirements.length) {
        return defaultValue;
      }
      return matchingRequirements.reduce(
        (amount, requirement) =>
          Math.max(amount, requirement.attributes.details.amount ?? 0),
        defaultValue,
      );
    },
  },
  providers: {
    providerToLabel(provider?: string) {
      let ret = "";
      switch (provider) {
        case "hetzner":
          ret = "Hetzner";
          break;
        case "aws":
          ret = "AWS";
          break;
        case "scaleway":
          ret = "Scaleway";
          break;
        case "outscale":
          ret = "Outscale";
          break;
      }
      return ret;
    },
    locationToCountryName(location: APIClusterLocation) {
      const country: Country | undefined = findCountryByDigram(
        location.attributes.country,
      );
      if (country) {
        return country.name.common;
      }
      return "";
    },
    locationToDisplayName(location: APIClusterLocation) {
      let countryName = "";
      const country: Country | undefined = findCountryByDigram(
        location.attributes.country,
      );

      if (country) {
        countryName = country.name.common;
      }

      return countryName + " (" + location.attributes.city + ")";
    },
  },
  prices: {
    format(price: number, currency?: string, precision?: number): string {
      const defaultPrecision = 0;
      const choosenPrecision =
        precision !== undefined ? precision : defaultPrecision;
      const multiplicator = Math.pow(10, choosenPrecision);
      const formatedPrice = (
        Math.round((price / 100) * multiplicator) / multiplicator
      ).toFixed(choosenPrecision);
      //@ts-ignore
      return (
        formatedPrice +
        (currency
          ? " " +
            enums.CurrencyTrigramToSymbol[
              currency as keyof typeof enums.CurrencyTrigramToSymbol
            ]
          : "")
      );
    },
  },
  dates: {
    formatDate(date: Date | null): string {
      if (!date) {
        return "";
      }
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },
    formatDateTime(time: string): string {
      const date = new Date(time);
      if (isNaN(date.getTime())) {
        return "";
      }
      return (
        date.getFullYear() +
        "-" +
        (date.getMonth() + 1 > 9
          ? date.getMonth() + 1
          : "0" + (date.getMonth() + 1)) +
        "-" +
        (date.getDate() > 9 ? date.getDate() : "0" + date.getDate()) +
        " " +
        (date.getHours() > 9 ? date.getHours() : "0" + date.getHours()) +
        ":" +
        (date.getMinutes() > 9 ? date.getMinutes() : "0" + date.getMinutes()) +
        ":" +
        (date.getSeconds() > 9 ? date.getSeconds() : "0" + date.getSeconds())
      );
    },
    formatTime(time: string): string {
      const date = new Date(time);
      return (
        (date.getHours() > 9 ? date.getHours() : "0" + date.getHours()) +
        ":" +
        (date.getMinutes() > 9 ? date.getMinutes() : "0" + date.getMinutes()) +
        ":" +
        (date.getSeconds() > 9 ? date.getSeconds() : "0" + date.getSeconds())
      );
    },
    formatTimestamp(time: string): string {
      const date = new Date(parseInt(time) * 1000);
      return (
        (date.getHours() > 9 ? date.getHours() : "0" + date.getHours()) +
        ":" +
        (date.getMinutes() > 9 ? date.getMinutes() : "0" + date.getMinutes()) +
        ":" +
        (date.getSeconds() > 9 ? date.getSeconds() : "0" + date.getSeconds())
      );
    },
  },
  states: {
    translateHealthCheckStatusToIcon(status?: string) {
      let ret = "spinner";
      switch (status) {
        case enums.HealthCheckStatus.OK:
          ret = "check-circle";
          break;
        case enums.HealthCheckStatus.PENDING:
          ret = "warning";
          break;
        case enums.HealthCheckStatus.FAILING:
          ret = "stopsign-alert";
          break;
        default:
          break;
      }
      return ret;
    },
    translateDeploymentStatusToLabel(status?: string): string {
      let ret = "Offline";

      switch (status) {
        case enums.DeploymentStatus.UNKNOWN:
          ret = "Unknown";
          break;
        case enums.DeploymentStatus.HEALTHY:
          ret = "Healthy";
          break;
        case enums.DeploymentStatus.DEGRADED:
          ret = "Degraded";
          break;
        case enums.DeploymentStatus.CRITICAL:
          ret = "Critical";
          break;
        case enums.DeploymentStatus.DEPLOYING:
          ret = "Deploying";
          break;
        case enums.DeploymentStatus.SHUTTING_DOWN:
          ret = "Shutting down";
          break;
        case enums.DeploymentStatus.OFFLINE:
          ret = "Offline";
          break;
        default:
          break;
      }

      return ret;
    },
    translateDeploymentStatusToSeverity(status?: string): string {
      let ret: string = enums.Severity.NEUTRAL;

      switch (status) {
        case enums.DeploymentStatus.HEALTHY:
          ret = enums.Severity.SUCCESS;
          break;
        case enums.DeploymentStatus.DEGRADED:
          ret = enums.Severity.WARNING;
          break;
        case enums.DeploymentStatus.CRITICAL:
          ret = enums.Severity.DANGER;
          break;
        case enums.DeploymentStatus.DEPLOYING:
          ret = enums.Severity.INFO;
          break;
        case enums.DeploymentStatus.SHUTTING_DOWN:
          ret = enums.Severity.NEUTRAL;
          break;
        default:
          break;
      }

      return ret;
    },
    translateDeploymentStatusToIcon(status?: string): string {
      let ret = "cloud-off";

      switch (status) {
        case enums.DeploymentStatus.UNKNOWN:
          ret = "clock";
          break;
        case enums.DeploymentStatus.OFFLINE:
          ret = "cloud-off";
          break;
        case enums.DeploymentStatus.HEALTHY:
          ret = "check-circle";
          break;
        case enums.DeploymentStatus.DEGRADED:
          ret = "warning";
          break;
        case enums.DeploymentStatus.CRITICAL:
          ret = "stopsign-alert";
          break;
        case enums.DeploymentStatus.DEPLOYING:
          ret = "spinner";
          break;
        case enums.DeploymentStatus.SHUTTING_DOWN:
          ret = "spinner";
          break;
        default:
          break;
      }

      return ret;
    },
    translateInvoiceStatusToSeverity(status?: string): string {
      let ret: string = enums.Severity.NEUTRAL;

      switch (status) {
        case "paid":
          ret = enums.Severity.SUCCESS;
          break;
        case "open":
          ret = enums.Severity.WARNING;
          break;
        case "uncollectible":
          ret = enums.Severity.DANGER;
          break;
        case "void":
          ret = enums.Severity.NEUTRAL;
          break;
        default:
          break;
      }

      return ret;
    },
    translateInvoiceStatusToLabel(status?: string): string {
      let ret = "Draft";
      switch (status) {
        case "paid":
          ret = "Paid";
          break;
        case "open":
          ret = "Open";
          break;
        case "uncollectible":
          ret = "Uncollectible";
          break;
        case "void":
          ret = "Canceled";
          break;
        default:
          break;
      }

      return ret;
    },
  },
  ressources: {
    isNewerThan(r1: metaGenerated, r2: metaGenerated): boolean {
      // For handeling ms in the format send by the API
      const troncR1a = r1.meta.generated_at.split(":");
      const troncR1b = troncR1a.splice(troncR1a.length - 2, 2);

      const troncR2a = r2.meta.generated_at.split(":");
      const troncR2b = troncR2a.splice(troncR2a.length - 2, 2);

      if (troncR1a.join(":") === troncR2a.join(":")) {
        return parseInt(troncR1b.join(":")) > parseInt(troncR2b.join(":"));
      }
      return new Date(troncR1a.join(":")) > new Date(troncR2a.join(":"));
    },
    isFromMe(g: metaGenerated): boolean {
      return g.meta.generated_by === store.getters["user/getUser"]?.id;
    },
    addMultipleRessourceToArray(
      arr: any[],
      newRessource: any[],
      editOnly?: boolean,
    ) {
      newRessource.forEach((ressource: any) => {
        if (ressource?.id) {
          const index = arr.findIndex((old: any) => old?.id === ressource.id);
          if (index !== -1) {
            if (helpers.ressources.isNewerThan(ressource, arr[index])) {
              arr[index] = ressource;
            }
          } else {
            if (!editOnly) {
              arr.push(ressource);
            }
          }
        }
      });
    },
    getOnlyDifferentFrom(newR: any, oldR: any): any {
      const ret: any = {};
      Object.keys(newR).forEach((newRKey: string) => {
        if (typeof newR[newRKey] === "object") {
          if (oldR[newRKey] && typeof oldR[newRKey] === "object") {
            const newOrdered = orderJSON(newR[newRKey]);
            const oldOrdered = orderJSON(oldR[newRKey]);
            if (JSON.stringify(newOrdered) !== JSON.stringify(oldOrdered)) {
              ret[newRKey] = newR[newRKey];
            }
          } else {
            ret[newRKey] = newR[newRKey];
          }
        } else if (oldR[newRKey] !== newR[newRKey]) {
          ret[newRKey] = newR[newRKey];
        }
      });
      return ret;
    },
  },
  contact: {
    contactUs() {
      window.open("mailto:support@fransys.cloud", "_blank");
    },
  },
  links: {
    newTab(url: string | undefined) {
      if (url) {
        window.open(url, "_blank");
      }
    },
    redirect(url: string | undefined) {
      if (url) {
        window.location.href = url;
      }
    },
  },
  subscription: {
    checkout() {
      return API.subscription
        .checkout()
        .then((urlRedirection: APIUrlRedirection) => {
          window.location.href = urlRedirection.attributes.url;
        })
        .catch();
    },
  },
  images: {
    dynamicLoad(path: string): string | undefined {
      try {
        return require(path) + "";
      } catch {
        return undefined;
      }
    },
  },
  clipboard: {
    copy(text: string) {
      const dummy = document.createElement("textarea");
      document.body.appendChild(dummy);
      dummy.value = text;
      dummy.select();
      document.execCommand("copy");
      document.body.removeChild(dummy);
    },
  },
  components: {
    getNameFromAttributesType(type: string) {
      const splitedType = type.split(/-|_/);
      const capitalized = splitedType.map((str: string) => {
        return str.charAt(0).toUpperCase() + str.slice(1);
      });
      return capitalized.join("");
    },
  },
  JSON: {
    diff(oldJSON: any, newJSON: any): any {
      return reconstructJSONFromDiff(diff(oldJSON, newJSON));
    },
    applyDiff(oldJSON: any, diffJSON: any): any {
      const tempJSON = JSON.parse(JSON.stringify(oldJSON));
      const diffDescriptor = removeDeleteKeyFromDiffDescriptor(
        diff(tempJSON, diffJSON),
      );
      return applyChangeset(tempJSON, diffDescriptor);
    },
  },
  console: {
    logLevel: 0,
    log(...messages: any) {
      if (this.logLevel === 0) {
        console.log(...messages);
      }
    },
    warn(...messages: any) {
      if (this.logLevel === 0) {
        console.warn(...messages);
      }
    },
    error(...messages: any) {
      if (this.logLevel === 0) {
        console.error(...messages);
      }
    },
  },
};

export default helpers;
